import { Form } from '@epages/react-components'
import type { List } from 'immutable'
import type { ReactElement } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useRef, useState } from 'react'

import { ProductSliderProductsSettings } from './ProductSliderProductsSettings'
import { loadProductsByIds } from '../../../../../store/actions'
import { useTranslate } from '../../../../../utils/translate'
import Button from '../../../../formComponents/Button'
import ButtonRow from '../../../../formComponents/ButtonRow'
import ProductSliderProductSizeSettings from './ProductListProductSizeSettings'
import createPromiseLinearizer from '../../../../../utils/createPromiseLinearizer'

const tabs: { [key: string]: (props: any) => ReactElement } = {
  productSliderProductsSetting: ProductSliderProductsSettings,
  productSliderProductSizeSetting: ProductSliderProductSizeSettings,
}

type Props = {
  selectedProducts: ImmutableList
} & WorkspacePluginSettingsProps

export default function ProductSliderSettings({
  config,
  data,
  onSave,
  onCancel,
  onDataChange,
  renderLayout,
  selectedProducts,
}: Readonly<Props>): ReactElement {
  const initialProductIds = useRef<List<string>>(data.get('productIds'))

  const t = useTranslate('interface')
  const scopedT = (key: string, options?: any) => t(`components.productSliderComponent.settingsModal.${key}`, options)

  const getProductDataLinearizer = createPromiseLinearizer()

  const dispatch = useDispatch()
  const sellingCountryId = useSelector(
    (state: State) => state.getIn(['shop', 'userSettings', 'countrySelection', 'sellingCountryId']) || null,
  )
  const getProductData = (pluginId: string, productIds: string[]) =>
    getProductDataLinearizer(() => Promise.resolve(dispatch(loadProductsByIds(pluginId, productIds, sellingCountryId))))

  const handleCancel = () => {
    if (data.get('productIds') !== initialProductIds.current) {
      getProductData(data.get('id'), initialProductIds.current.toJS())
    }
    onCancel()
  }

  const [activeTab, setActiveTab] = useState('productSliderProductsSetting')
  const Setting = tabs[activeTab]

  return (
    <Form
      name="pluginProductSlider"
      value={data.set('imageSize', data.get('imageSize', 'S'))}
      onChange={onDataChange}
      onSubmit={onSave}
    >
      {renderLayout({
        title:
          selectedProducts.size > 0 ? scopedT(`${activeTab}.title.onEdit`) : scopedT(`${activeTab}.title.onCreate`),
        tabs: Object.keys(tabs).map((tab) => ({
          name: tab,
          title: scopedT(`${tab}.title.onEdit`),
          active: activeTab === tab,
          onClick: (name) => setActiveTab(name),
        })),
        body: (
          <Setting
            t={scopedT}
            config={config}
            data={data}
            selectedProducts={selectedProducts}
            getProductData={getProductData}
          />
        ),
        footer: (
          <ButtonRow>
            <Button variant="cancel" type="button" onClick={handleCancel}>
              {scopedT('cancelButton.label')}
            </Button>
            <Button>{scopedT('saveButton.label')}</Button>
          </ButtonRow>
        ),
      })}
    </Form>
  )
}
